import paginatedResponseParser from '../_commonParsers/paginatedResponseParser';
import type { PaginatedData } from '../_types';
import {
  toExerciseAttemptId,
  toExerciseQueueCurrentItemId,
  toExerciseQueueItemId,
  toExerciseQueueUuid,
  toExerciseUuid,
} from './exerciseQueues.branded';
import type {
  ExerciseAttempt,
  ExerciseAttemptDto,
  ExerciseQueue,
  ExerciseQueueAnswerResponse,
  ExerciseQueueAnswerResponseDto,
  ExerciseQueueCreateDto,
  ExerciseQueueCreateFormInput,
  ExerciseQueueCurrentItem,
  ExerciseQueueCurrentItemDto,
  ExerciseQueueDto,
  ExerciseQueueItem,
  ExerciseQueueItemDto,
  ExerciseQueueList,
  ExerciseQueueListDto,
  ExerciseQueueProgress,
  ExerciseQueueProgressDto,
  PaginatedExerciseQueueListDto,
} from './exerciseQueues.types';

const parseDate = (value: string | null): Date | null => (value == null ? null : new Date(value));

const parseExerciseAttemptForUI = (raw: ExerciseAttemptDto): ExerciseAttempt => ({
  id: toExerciseAttemptId(raw.id),
  answer: raw.answer,
  correct: raw.correct,
  createdAt: parseDate(raw.created_at),
  correctAnswerIds: raw.correct_answer_ids ?? [],
});

const parseExerciseQueueProgressForUI = (raw: ExerciseQueueProgressDto): ExerciseQueueProgress => ({
  answered: raw.answered,
  total: raw.total,
  correct: raw.correct ?? 0,
  incorrect: raw.incorrect ?? 0,
});

const parseExerciseQueueItemForUI = (raw: ExerciseQueueItemDto): ExerciseQueueItem => ({
  id: toExerciseQueueItemId(raw.id),
  order: raw.order,
  exerciseUuid: toExerciseUuid(raw.exercise_uuid),
  answered: raw.answered,
  correct: raw.correct,
  submittedAnswer: raw.submitted_answer,
  correctAnswerIds: raw.correct_answer_ids ?? [],
});

const parseExerciseQueueCurrentItemForUI = (raw: ExerciseQueueCurrentItemDto): ExerciseQueueCurrentItem => ({
  id: toExerciseQueueCurrentItemId(raw.id),
  order: raw.order,
  exerciseUuid: toExerciseUuid(raw.exercise_uuid),
  latestAttempt: raw.latest_attempt ? parseExerciseAttemptForUI(raw.latest_attempt) : null,
});

export const parseExerciseQueueForUI = (raw: ExerciseQueueDto): ExerciseQueue => ({
  uuid: toExerciseQueueUuid(raw.uuid),
  subject: raw.subject,
  active: raw.active,
  filters: raw.filters,
  createdAt: parseDate(raw.created_at),
  completedAt: parseDate(raw.completed_at),
  currentItem: raw.current_item == null ? null : parseExerciseQueueCurrentItemForUI(raw.current_item),
  items: raw.items.map(parseExerciseQueueItemForUI),
  progress: parseExerciseQueueProgressForUI(raw.progress),
  feedback: raw.feedback ?? null,
});

export const parseExerciseQueueListForUI = (raw: ExerciseQueueListDto): ExerciseQueueList => ({
  uuid: toExerciseQueueUuid(raw.uuid),
  subject: raw.subject,
  active: raw.active,
  createdAt: parseDate(raw.created_at),
  completedAt: parseDate(raw.completed_at),
  progress: parseExerciseQueueProgressForUI(raw.progress),
});

export const parseExerciseQueueAnswerResponseForUI = (raw: ExerciseQueueAnswerResponseDto): ExerciseQueueAnswerResponse => ({
  attempt: parseExerciseAttemptForUI(raw.attempt),
  queue: parseExerciseQueueForUI(raw.queue),
});

export const parsePaginatedExerciseQueueListForUI = (raw: PaginatedExerciseQueueListDto): PaginatedData<ExerciseQueueList> =>
  paginatedResponseParser(raw, items => items.map(parseExerciseQueueListForUI));

export const parseExerciseQueueCreateFormInputForAPI = (raw: ExerciseQueueCreateFormInput): ExerciseQueueCreateDto => {
  const sections: number[] = [];
  const subsections: number[] = [];
  Object.entries(raw.scope.sections).forEach(([sectionId, section]) => {
    if (section.allSelected === 'checked') {
      sections.push(Number(sectionId));
    } else if (section.allSelected === 'indeterminate') {
      Object.entries(section.subsections).forEach(([subsectionId, subsection]) => {
        if (subsection) {
          subsections.push(Number(subsectionId));
        }
      });
    }
  });
  return {
    subject: raw.subject.pk,
    sort: raw.sort,
    size: raw.useAllExercises ? null : raw.queueSize,
    unanswered_only: raw.unansweredOnly,
    include_correctly_answered: raw.includeCorrectlyAnswered,
    include_incorrectly_answered: raw.includeIncorrectlyAnswered,
    subsections,
    sections,
  };
};
